import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import { Logo } from "@/components/Logo";
import { Menu, Phone, LogIn, ChevronDown } from "lucide-react";

const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

// Shorter labels + curated inline set (the red "Free Consultation" button covers that CTA).
const SHORT_LABELS = {
  "Trilogy Senior Wellness": "Trilogy Wellness",
  "Resources / Q&A": "Resources",
};
const HIDE_INLINE = new Set(["Free Consultation", "Resources / Q&A", "Service Areas"]);

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const PublicNav = ({ nav = [], phone }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href, newTab = false) => {
    setOpen(false);
    if (!href) return;
    const external = /^(https?:|mailto:|tel:)/i.test(href);
    if (external || newTab) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    if (href.startsWith("/#") || href.startsWith("#")) {
      const id = href.replace(/^\/?#/, "");
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToId(id), 120);
      } else {
        scrollToId(id);
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <header
      data-testid="public-nav"
      className={`sticky top-0 z-50 border-b backdrop-blur-md transition-shadow ${
        scrolled
          ? "bg-white/80 border-[#1C4358]/12 shadow-sm shadow-[#1C4358]/10"
          : "bg-white/80 border-[#1C4358]/8"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px] gap-3">
          <button
            type="button"
            data-testid="nav-logo-home"
            onClick={() => go("/")}
            className="flex items-center gap-2.5 shrink-0"
            aria-label="MovingMyMama home"
          >
            <Logo size={42} />
            <span className="font-display font-bold text-[17px] text-[#1C4358] hidden sm:block">
              MovingMyMama
            </span>
          </button>

          <nav className="hidden xl:flex items-center gap-0.5" aria-label="Primary">
            {nav.filter((item) => !HIDE_INLINE.has(item.label)).map((item) => {
              const kids = (item.children || []).filter(Boolean);
              const key = item.href || item.label;
              if (kids.length) {
                return (
                  <div key={key} className="relative group">
                    <button
                      type="button"
                      data-testid={`nav-dd-${(item.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      aria-haspopup="true"
                      className="mmm-tap mmm-navlink inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-2 rounded-lg text-[14px] font-medium text-[#252221] hover:text-[#1C4358] transition-colors"
                    >
                      {SHORT_LABELS[item.label] || item.label}
                      <ChevronDown size={14} className="opacity-70" />
                    </button>
                    <div className="absolute left-0 top-full pt-2 z-50 opacity-0 invisible translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0">
                      <div className="min-w-[210px] rounded-xl border border-[#1C4358]/12 bg-white shadow-lg shadow-[#1C4358]/15 p-1.5">
                        {kids.map((c) => (
                          <button
                            key={c.href || c.label}
                            type="button"
                            data-testid={`nav-dd-item-${(c.href || c.label).replace(/[/#]/g, "") || "home"}`}
                            onClick={() => go(c.href, c.new_tab)}
                            className="mmm-tap w-full text-left px-3 py-2 rounded-lg text-[14px] font-medium text-[#252221] hover:bg-[#1C4358]/6 hover:text-[#1C4358] transition-colors"
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`nav-link-${(item.href || item.label).replace(/[/#]/g, "") || "home"}`}
                  onClick={() => go(item.href, item.new_tab)}
                  className="mmm-tap mmm-navlink whitespace-nowrap px-2.5 py-2 rounded-lg text-[14px] font-medium text-[#252221] hover:text-[#1C4358] transition-colors"
                >
                  {SHORT_LABELS[item.label] || item.label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-1.5">
            <a
              href={telHref(phone)}
              data-testid="nav-call-btn"
              className="mmm-tap hidden 2xl:inline-flex items-center gap-2 px-3.5 h-11 rounded-full text-[15px] font-semibold text-[#1C4358] border border-[#1C4358]/20 hover:bg-[#1C4358]/6 transition-colors whitespace-nowrap"
            >
              <Phone size={16} className="shrink-0" /> {phone}
            </a>
            <button
              type="button"
              data-testid="nav-consult-btn"
              onClick={() => go("/book")}
              className="mmm-tap hidden sm:inline-flex items-center whitespace-nowrap px-4 h-11 rounded-full text-[15px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors"
            >
              Free Consultation
            </button>
            <AccessibilityToolbar />
            <button
              type="button"
              data-testid="nav-login-btn"
              onClick={() => go("/login")}
              className="mmm-tap hidden 2xl:inline-flex items-center gap-1.5 px-3 h-11 rounded-lg text-[15px] font-medium text-[#1C4358] hover:bg-[#1C4358]/6 transition-colors"
            >
              <LogIn size={16} /> Login
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="nav-mobile-toggle"
                  aria-label="Open menu"
                  className="mmm-tap xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg text-[#1C4358] hover:bg-[#1C4358]/8 transition-colors"
                >
                  <Menu size={24} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[86vw] max-w-sm p-0 bg-white flex flex-col h-full" data-testid="nav-mobile-drawer">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SheetDescription className="sr-only">Main site navigation links and actions</SheetDescription>
                <div className="flex items-center gap-2.5 px-5 h-[60px] border-b border-[#1C4358]/10 shrink-0">
                  <Logo size={34} />
                  <span className="font-display font-bold text-[16px] text-[#1C4358]">MovingMyMama</span>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 pb-28 flex flex-col gap-0.5" data-testid="nav-mobile-scroll">
                  {nav.map((item) => {
                    const kids = (item.children || []).filter(Boolean);
                    const key = item.href || item.label;
                    return (
                      <React.Fragment key={key}>
                        <button
                          type="button"
                          data-testid={`mobile-nav-link-${(item.href || item.label).replace(/[/#]/g, "") || "home"}`}
                          onClick={() => (item.href ? go(item.href, item.new_tab) : null)}
                          className="mmm-tap text-left px-4 py-2 rounded-xl text-[16px] font-semibold text-[#252221] hover:bg-[#1C4358]/6 transition-colors"
                        >
                          {item.label}
                        </button>
                        {kids.map((c) => (
                          <button
                            key={c.href || c.label}
                            type="button"
                            data-testid={`mobile-nav-child-${(c.href || c.label).replace(/[/#]/g, "") || "home"}`}
                            onClick={() => go(c.href, c.new_tab)}
                            className="mmm-tap text-left pl-8 pr-4 py-1.5 rounded-xl text-[14px] font-medium text-[#4B5563] hover:bg-[#1C4358]/6 transition-colors"
                          >
                            {c.label}
                          </button>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  <div className="h-px bg-[#1C4358]/10 my-2" />
                  <button
                    type="button"
                    data-testid="mobile-nav-consult"
                    onClick={() => go("/book")}
                    className="mmm-tap px-4 py-3 rounded-xl text-[16px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors"
                  >
                    Book a Free Consultation
                  </button>
                  <a
                    href={telHref(phone)}
                    data-testid="mobile-nav-call"
                    className="mmm-tap flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[16px] font-semibold text-[#1C4358] border border-[#1C4358]/25"
                  >
                    <Phone size={18} /> Call {phone}
                  </a>
                  <button
                    type="button"
                    data-testid="mobile-nav-login"
                    onClick={() => go("/login")}
                    className="mmm-tap flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[16px] font-medium text-[#1C4358]"
                  >
                    <LogIn size={18} /> Staff / Portal Login
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
